/**
 * IndexedDB 유틸리티 클래스
 * AI 번역 언어 설정 저장/조회용
 */
class IndexedDBUtil {
  constructor() {
    this.dbName = 'maot_ai_translate';
    this.storeName = 'language_settings';
    this.version = 1;
    this.db = null;
  }

  /**
   * IndexedDB 초기화
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('IndexedDB 열기 실패:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // 기존 store가 있으면 삭제
        if (db.objectStoreNames.contains(this.storeName)) {
          db.deleteObjectStore(this.storeName);
        }

        // 새로운 object store 생성
        const store = db.createObjectStore(this.storeName, { keyPath: 'userId' });
        store.createIndex('userId', 'userId', { unique: true });
      };
    });
  }

  /**
   * 사용자의 언어 설정 저장
   * @param {string} userId - 사용자 ID
   * @param {object} languageSettings - 언어 설정 객체
   * @param {string} languageSettings.originLang - 원본 언어
   * @param {string} languageSettings.newLang - 번역 언어
   */
  async saveLanguageSettings(userId, languageSettings) {
    try {
      if (!this.db) {
        await this.init();
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const data = {
        userId: userId,
        originLang: languageSettings.originLang,
        newLang: languageSettings.newLang,
        lastUpdated: new Date().toISOString()
      };

      return new Promise((resolve, reject) => {
        const request = store.put(data);

        request.onsuccess = () => {
          console.log('언어 설정 저장 완료:', data);
          resolve(data);
        };

        request.onerror = () => {
          console.error('언어 설정 저장 실패:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('언어 설정 저장 중 오류:', error);
      throw error;
    }
  }

  /**
   * 사용자의 언어 설정 조회
   * @param {string} userId - 사용자 ID
   * @returns {Promise<object|null>} 언어 설정 객체 또는 null
   */
  async getLanguageSettings(userId) {
    try {
      if (!this.db) {
        await this.init();
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.get(userId);

        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            console.log('언어 설정 조회 완료:', result);
            resolve({
              originLang: result.originLang,
              newLang: result.newLang,
              lastUpdated: result.lastUpdated
            });
          } else {
            console.log('저장된 언어 설정이 없습니다.');
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('언어 설정 조회 실패:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('언어 설정 조회 중 오류:', error);
      throw error;
    }
  }

  /**
   * 사용자의 언어 설정 삭제
   * @param {string} userId - 사용자 ID
   */
  async deleteLanguageSettings(userId) {
    try {
      if (!this.db) {
        await this.init();
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.delete(userId);

        request.onsuccess = () => {
          console.log('언어 설정 삭제 완료:', userId);
          resolve();
        };

        request.onerror = () => {
          console.error('언어 설정 삭제 실패:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('언어 설정 삭제 중 오류:', error);
      throw error;
    }
  }

  /**
   * IndexedDB 연결 종료
   */
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// 싱글톤 인스턴스 생성
const indexedDBUtil = new IndexedDBUtil();

export default indexedDBUtil;
